/**
 * The Permit workspace's server state: the design (editor document of the current version), the free preview
 * (checks, SLD, thumbnails), the permit site details and photos, the SLD edits, the workspace's draft (the request
 * inputs and the step, `…/permit-draft`) and the plan-set history; plus the autosave every edit goes through
 * (debounced, flushed when leaving, a keepalive request when the page unloads; lib/autosave.ts).
 */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { api, unwrap, type S } from "../api/client";
import { Autosaver, type SaveState } from "../lib/autosave";

export { requestBody, type Inputs } from "../lib/permitDraft";
export type { SaveState } from "../lib/autosave";

/** The workspace's draft of a design (request inputs and step), as saved on the server. */
export function usePermitDraft(designId: string) {
  return useQuery({
    queryKey: ["permit-draft", designId],
    queryFn: () => unwrap(api.GET("/v1/designs/{design_id}/permit-draft", { params: { path: { design_id: designId } } })),
    staleTime: 0,
  });
}

export function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = window.setTimeout(() => setV(value), ms);
    return () => window.clearTimeout(t);
  }, [value, ms]);
  return v;
}

export function useEditorDoc(designId: string) {
  return useQuery({
    queryKey: ["editor", designId, null],
    queryFn: () => unwrap(api.GET("/v1/designs/{design_id}/editor", { params: { path: { design_id: designId }, query: {} } })),
  });
}

/** The free preview. `thumbnails` (Review step) renders every page as a PNG; without, it is ~50 ms. */
/** `rev` counts the saves of the SLD edits (the answer carries it: which edits its drawing has). */
export function usePreview(designId: string, version: number | undefined, body: S["PlanSetInputs"], thumbnails: number | null, rev = 0) {
  return useQuery({
    queryKey: ["plan-set-preview", designId, version, body, thumbnails, rev],
    enabled: !!version,
    queryFn: async () => ({
      ...(await unwrap(
        api.POST("/v1/designs/{design_id}/versions/{version_number}/plan-set/preview", {
          params: { path: { design_id: designId, version_number: version! }, query: thumbnails ? { thumbnail_width: thumbnails } : { thumbnails: false } },
          body,
        }),
      )),
      rev,
    }),
    placeholderData: (prev) => prev,
    retry: false,
    staleTime: 30_000,
  });
}

export function usePlanSets(designId: string) {
  return useQuery({
    queryKey: ["plan-sets", designId],
    queryFn: () => unwrap(api.GET("/v1/designs/{design_id}/plan-sets", { params: { path: { design_id: designId } } })),
    refetchInterval: (q) => (q.state.data?.items.some((p) => p.status === "pending" || p.status === "running") ? 3000 : false),
  });
}

export function usePermitSite(designId: string, version: number | undefined) {
  return useQuery({
    queryKey: ["permit-site", designId, version],
    enabled: !!version,
    queryFn: () => unwrap(api.GET("/v1/designs/{design_id}/permit-site", { params: { path: { design_id: designId }, query: { version_number: version! } } })),
  });
}

export function useSldState(designId: string, version: number | undefined) {
  return useQuery({
    queryKey: ["sld-overrides", designId, version],
    enabled: !!version,
    queryFn: () => unwrap(api.GET("/v1/designs/{design_id}/sld-overrides", { params: { path: { design_id: designId }, query: { version_number: version! } } })),
  });
}

export function usePermitEquipment(country: string | undefined) {
  return useQuery({
    queryKey: ["permit-equipment", country ?? "all"],
    queryFn: () => unwrap(api.GET("/v1/permit-equipment", { params: { query: country ? { country } : {} } })),
    staleTime: 5 * 60_000,
  });
}

/** Re-runs the preview (and the summary's) after the site details, photos or SLD edits changed on the server. */
export function useRefreshPreview(designId: string) {
  const qc = useQueryClient();
  return useCallback(() => {
    qc.invalidateQueries({ queryKey: ["plan-set-preview", designId] });
  }, [qc, designId]);
}

export interface Autosave {
  state: SaveState;
  error: unknown;
  /** Saves the last change now; resolves when the server has it (or the save failed). */
  flush: () => Promise<void>;
  /** As `flush`, but rejects when the change could not be saved (leaving for billing must not lose it). */
  flushOrThrow: () => Promise<void>;
}

/**
 * Saves `value` `delay` ms after its last change when it differs from what the server has (`signature`); the first
 * non-null value is what the server has. Nothing is lost on leaving: unmounting (a route change) saves at once, and
 * when the page unloads (tab close, reload, Stripe Checkout) `unload` sends the unsaved value with a keepalive
 * request (else the browser asks before leaving). `value` null: nothing to save yet (loading, or read only).
 * `serverSig`: the signature of what the server has, when the first value may already differ from it (e.g. the
 * step of a checkout return); else the first value is taken as the server's.
 */
export function useAutosave<T>(value: T | null, signature: (v: T) => string, save: (v: T) => Promise<unknown>, delay = 700, unload?: (v: T) => boolean, serverSig?: string): Autosave {
  const [view, setView] = useState<{ state: SaveState; error: unknown }>({ state: "saved", error: null });
  const saveRef = useRef(save);
  saveRef.current = save;
  const unloadRef = useRef(unload);
  unloadRef.current = unload;
  const saver = useRef<Autosaver<T> | null>(null);
  if (!saver.current)
    saver.current = new Autosaver<T>({
      delay,
      save: (v) => saveRef.current(v),
      unload: (v) => (unloadRef.current ? unloadRef.current(v) : false),
      onState: (state, error) => setView({ state, error }),
    });
  const sig = value == null ? null : signature(value);
  const started = useRef(false);
  useEffect(() => {
    if (value == null || sig == null) return;
    if (!started.current && serverSig != null) saver.current!.init(value, serverSig);
    started.current = true;
    saver.current!.update(value, sig);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sig]);
  useEffect(() => {
    const s = saver.current!;
    const onHide = () => void s.unload();
    const onBefore = (e: BeforeUnloadEvent) => {
      if (!s.unload()) e.preventDefault(); // could not hand the change over: let the browser ask
    };
    window.addEventListener("pagehide", onHide);
    window.addEventListener("beforeunload", onBefore);
    return () => {
      window.removeEventListener("pagehide", onHide);
      window.removeEventListener("beforeunload", onBefore);
      void s.flush(); // leaving the page in the app: save the last change now
    };
  }, []);
  const flush = useCallback(() => saver.current!.flush(), []);
  const flushOrThrow = useCallback(() => saver.current!.flushOrThrow(), []);
  return useMemo(() => ({ state: view.state, error: view.error, flush, flushOrThrow }), [view, flush, flushOrThrow]);
}

/** Open map data around the design (parcel, nearby roads, the street) for the Site step's plan. */
export function useOpenData(designId: string, version: number | undefined) {
  return useQuery({
    queryKey: ["permit-open-data", designId, version],
    enabled: !!version,
    queryFn: () =>
      unwrap(api.GET("/v1/designs/{design_id}/permit-site/open-data", { params: { path: { design_id: designId }, query: { version_number: version! } } })),
    staleTime: 10 * 60_000,
    retry: false,
  });
}

/** Rails, attachments and rapid-shutdown devices, with the manufacturer documents uploaded for each. */
export function useRacking() {
  return useQuery({
    queryKey: ["permit-racking"],
    queryFn: () => unwrap(api.GET("/v1/permit-racking")),
    staleTime: 5 * 60_000,
  });
}

/** The Equipment step's typeahead (`…/permit-equipment/search`): the catalogue, the CEC lists and the company's own. */
export function useEquipmentSearch(orgId: string, q: string, kinds: string[], country: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ["permit-equipment-search", orgId, q, kinds.join(","), country ?? ""],
    enabled,
    queryFn: () =>
      unwrap(
        api.GET("/v1/orgs/{org_id}/permit-equipment/search", {
          params: { path: { org_id: orgId }, query: { q: q || undefined, kind: kinds.join(","), country, limit: 20 } },
        }),
      ),
    placeholderData: (prev) => prev,
    staleTime: 60_000,
  });
}

/** One equipment item (the chosen module, inverter or optimizer). */
export function useEquipmentItem(id: string | null | undefined) {
  return useQuery({
    queryKey: ["permit-equipment-item", id],
    enabled: !!id,
    queryFn: () => unwrap(api.GET("/v1/permit-equipment/{equipment_id}", { params: { path: { equipment_id: id! } } })),
    staleTime: 5 * 60_000,
  });
}
